import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Wrench, AlertTriangle, CheckCircle2, Camera, Clock } from 'lucide-react';
import VehicleBrandLogo from './VehicleBrandLogo';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { assistAPI } from '../services/api';
import { useT, i18n } from '../services/i18n';

const MileageForm = ({ vehicle, onSaved }) => {
  const { t } = useT();
  const [km, setKm] = useState(vehicle.mileage ?? '');
  const [yearly, setYearly] = useState(vehicle.yearly_km ?? 15000);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await assistAPI.setMileage(vehicle.id, Number(km), Number(yearly)); onSaved(); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid={`mileage-form-${vehicle.id}`}>
      <div><label className="block text-xs text-gray-600 mb-1">{t('mb.mileage')}</label><Input type="number" value={km} onChange={(e) => setKm(e.target.value)} placeholder={t('mb.mileagePh')} className="w-36" data-testid="mileage-input" /></div>
      <div><label className="block text-xs text-gray-600 mb-1">{t('mb.yearly')}</label><Input type="number" value={yearly} onChange={(e) => setYearly(e.target.value)} className="w-32" data-testid="yearly-km-input" /></div>
      <Button size="sm" onClick={save} disabled={busy || km === ''} className="bg-orange-500 hover:bg-orange-600" data-testid="mileage-save">{busy ? <Loader2 size={14} className="animate-spin" /> : t('mb.save')}</Button>
    </div>
  );
};

const DueRow = ({ item, vehicle }) => {
  const { t } = useT();
  const tone = item.overdue ? 'text-red-700 bg-red-50' : item.due_soon ? 'text-amber-700 bg-amber-50' : 'text-gray-700 bg-gray-50';
  const when = [item.remaining_km != null && t(item.remaining_km < 0 ? 'mb.overdueKm' : 'mb.inKm', { n: Math.abs(item.remaining_km).toLocaleString(i18n.locale()) }), item.remaining_days != null && t(item.remaining_days < 0 ? 'mb.overdueDays' : 'mb.inDays', { n: Math.abs(item.remaining_days) })].filter(Boolean).join(' · ');
  return (
    <li className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm ${tone}`} data-testid={`due-item-${item.subcategory}`}>
      <span className="flex items-center gap-2">{item.overdue ? <AlertTriangle size={15} /> : item.due_soon ? <Clock size={15} /> : <CheckCircle2 size={15} className="text-green-600" />}<strong>{item.subcategory}</strong><span className="text-xs opacity-75">{when}{item.last_purchase ? ` · ${t('mb.lastPurchase', { date: item.last_purchase })}` : ''}</span></span>
      <Link to={`/pieces/${item.category}?brand=${encodeURIComponent(vehicle.brand)}&model=${encodeURIComponent(vehicle.model)}&year=${vehicle.year}`} className="text-xs text-orange-600 hover:underline whitespace-nowrap">{t('mb.seeParts')}</Link>
    </li>
  );
};

export const MaintenanceBook = () => {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [diags, setDiags] = useState([]);
  const load = () => { assistAPI.maintenance().then(setData).catch(() => setData({ vehicles: [], alerts: [] })); assistAPI.myDiagnostics().then(setDiags).catch(() => {}); };
  useEffect(load, []);
  if (!data) return <div className="p-6 text-gray-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> {t('common.loading')}</div>;

  return (
    <div className="space-y-6" data-testid="maintenance-book">
      {data.vehicles.length === 0 && <p className="text-sm text-gray-600">{t('mb.noVehicle1')} <Link to="/mon-compte/garage" className="text-orange-600 underline">{t('mb.noVehicleLink')}</Link> {t('mb.noVehicle2')}</p>}
      {data.vehicles.map(({ vehicle, items }) => (
        <section key={vehicle.id} className="bg-white rounded-xl border p-5 space-y-4" data-testid={`maintenance-vehicle-${vehicle.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2"><VehicleBrandLogo brand={vehicle.brand} model={vehicle.model} size={44} preferModel zoom testId={`maintenance-vehicle-logo-${vehicle.id}`} /> {vehicle.brand} {vehicle.model} {vehicle.year}{vehicle.engine_name ? ` — ${vehicle.engine_name}` : ''}</h3>
            {vehicle.estimated_mileage != null && <span className="text-sm text-gray-600">≈ <strong data-testid="estimated-mileage">{vehicle.estimated_mileage.toLocaleString(i18n.locale())} km</strong> {t('mb.estimated')}</span>}
          </div>
          <MileageForm vehicle={vehicle} onSaved={load} />
          {vehicle.mileage == null ? (
            <p className="text-xs text-gray-500">{t('mb.enterMileage')}</p>
          ) : (
            <ul className="space-y-1.5">{items.map((it) => <DueRow key={it.rule_id} item={it} vehicle={vehicle} />)}</ul>
          )}
        </section>
      ))}
      {data.alerts.length > 0 && (
        <section className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><Wrench size={18} className="text-blue-600" /> {t('mb.alertsTitle')}</h3>
          <ul className="divide-y text-sm">{data.alerts.map((a) => <li key={a.id} className="py-2 flex justify-between gap-3"><span>{a.subcategory} <span className="text-xs text-gray-500">{a.created_at.slice(0, 10)}</span></span><Link to={a.url} className="text-orange-600 text-xs hover:underline">{t('mb.see')}</Link></li>)}</ul>
        </section>
      )}
      <section className="bg-white rounded-xl border p-5" data-testid="my-diagnostics">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><Camera size={18} className="text-orange-500" /> {t('mb.diagTitle')}</h3>
        {diags.length === 0 ? <p className="text-sm text-gray-500">{t('mb.diagEmpty')}</p> : (
          <ul className="divide-y text-sm">
            {diags.map((d) => (
              <li key={d.id} className="py-2 flex items-center gap-3" data-testid={`diagnostic-${d.id}`}>
                <img src={`${process.env.REACT_APP_BACKEND_URL}${d.image_url}`} alt="" className="w-12 h-12 rounded object-cover bg-gray-100" />
                <span className="flex-1 min-w-0">
                  <span className="block font-medium">{d.product_name || t('mb.partToIdentify')} <span className={`ml-1 text-[11px] px-1.5 py-0.5 rounded ${d.status === 'confirmed' ? 'bg-green-100 text-green-700' : d.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{d.status === 'confirmed' ? t('mb.confirmed') : d.status === 'rejected' ? t('mb.rejected') : t('mb.pending')}</span></span>
                  <span className="block text-xs text-gray-500 flex items-center gap-1 flex-wrap">{d.created_at.slice(0, 10)} · {d.vehicle ? <><VehicleBrandLogo brand={d.vehicle.brand} bare size={14} fallbackClass="text-gray-400" testId={`diag-vehicle-logo-${d.id}`} /> {d.vehicle.brand} {d.vehicle.model}</> : ''} {d.final_reference ? `· ${t('mb.ref', { ref: d.final_reference })}` : d.ai_reference ? `· ${t('mb.aiLead', { ref: d.ai_reference, c: d.confidence })}` : ''}</span>
                </span>
                {d.final_product_slug && <Link to={`/produit/${d.final_product_slug}`} className="text-xs text-orange-600 hover:underline">{t('mb.seePart')}</Link>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default MaintenanceBook;
